
import { useMemo } from 'react';
import { StudentWithAttendance } from '@/types/attendance';
import { LateRecord } from '@/hooks/useLateAttendance';
import { 
  calculateOverallStats, 
  calculateGradeData 
} from '@/utils/reportCalculations';
import { 
  filterStudentsByStageGradeSection,
  getStudentsWithAlerts,
  getStudentsWithLateAlerts,
  getMostAbsentStudents,
  getMostLateStudents,
  getBestAttendanceStudents
} from '@/utils/reportFilters';
import { 
  transformStudentsWithLateData, 
  generateWeeklyData 
} from '@/utils/reportDataTransformers';

export type { StudentWithAttendance };

export const useReportsData = (
  studentsWithAttendance: StudentWithAttendance[],
  selectedStage: string,
  selectedGrade: string,
  selectedSection: string,
  enabledStages: any[],
  attendanceRecords: any[],
  lateRecords?: LateRecord[],
  periodDateRange?: { startDate: Date; endDate: Date },
  alertSettings?: any
) => {
 
  // console.log('useReportsData called with', {
  //   studentsWithAttendanceLength: studentsWithAttendance.length,
  //   selectedStage,
  //   selectedGrade,
  //   selectedSection,
  //   enabledStagesLength: enabledStages.length,
  //   attendanceRecordsLength: attendanceRecords.length,
  //   lateRecordsLength: lateRecords?.length,
  //   periodDateRange,
  //   alertSettings
  // });

  // إصلاح مشكلة date range
  const validPeriodDateRange = useMemo(() => {
    if (!periodDateRange) return undefined;
    
    // التأكد من أن startDate و endDate مختلفان
    const { startDate, endDate } = periodDateRange;
    if (startDate.getTime() === endDate.getTime()) {
      // إذا كانا متشابهين، جعل endDate في نهاية اليوم
      const newEndDate = new Date(startDate);
      newEndDate.setHours(23, 59, 59, 999);
      return { startDate, endDate: newEndDate };
    }
    
    return periodDateRange;
  }, [periodDateRange]);
  // Transform students with correct late data مع cache
  const studentsWithLateData = useMemo(() => {
    // const cacheKey = `late-data-${studentsWithAttendance.length}-${lateRecords?.length || 0}-${validPeriodDateRange?.startDate.getTime()}-${validPeriodDateRange?.endDate.getTime()}`;
    
    // const cached = cache.get(cacheKey);
    // if (cached) return cached;
    
    if (!lateRecords) return studentsWithAttendance;
    
    const result = transformStudentsWithLateData(studentsWithAttendance, lateRecords, validPeriodDateRange);
    // cache.set(cacheKey, result);
    
    return result;
  }, [studentsWithAttendance, lateRecords, validPeriodDateRange]);

  // Filter students based on selected stage, grade, and section مع cache
  const filteredStudents = useMemo(() => {
    // const cacheKey = `filtered-${studentsWithLateData.length}-${selectedStage}-${selectedGrade}-${selectedSection}`;
    
    // const cached = cache.get(cacheKey);
    // if (cached) return cached;
    
    const filtered = filterStudentsByStageGradeSection(
      studentsWithLateData,
      selectedStage,
      selectedGrade,
      selectedSection,
      enabledStages
    );
    
    // cache.set(cacheKey, filtered);
    return filtered;
  }, [studentsWithLateData, selectedStage, selectedGrade, selectedSection, enabledStages]);

  // Students with alerts based on settings
  const studentsWithAlerts = useMemo(() => 
    getStudentsWithAlerts(filteredStudents, alertSettings),
    [filteredStudents, alertSettings]
  );

  const studentsWithLateAlerts = useMemo(() => 
    getStudentsWithLateAlerts(filteredStudents, alertSettings),
    [filteredStudents, alertSettings]
  );

  // Most absent students (top 5)
  const mostAbsentStudents = useMemo(() => 
    getMostAbsentStudents(filteredStudents),
    [filteredStudents]
  );

  // Most late students (top 5)
  const mostLateStudents = useMemo(() => {
    const sorted = getMostLateStudents(filteredStudents);
    
    // console.log('Most late students (corrected):', sorted.map(s => ({
    //   name: s.name,
    //   lateDays: s.lateDays
    // })));
    
    return sorted;
  }, [filteredStudents]);

  // Best attendance students (top 5)
  const bestAttendanceStudents = useMemo(() => 
    getBestAttendanceStudents(filteredStudents),
    [filteredStudents]
  );

  // Weekly attendance data مع cache
  const weeklyData = useMemo(() => {
    // const cacheKey = `weekly-${attendanceRecords.length}`;
    // const cached = cache.get(cacheKey);
    // if (cached) return cached;
    
    const result = generateWeeklyData(attendanceRecords);
    // cache.set(cacheKey, result);
    return result;
  }, [attendanceRecords,]);

  // Grade data for charts مع cache
  const gradeData = useMemo(() => {
    // const cacheKey = `grade-${studentsWithAttendance.length}-${selectedStage}`;
    // const cached = cache.get(cacheKey);
    // if (cached) return cached;
    
    const result = calculateGradeData(studentsWithAttendance, selectedStage, enabledStages);
    // cache.set(cacheKey, result);
    return result;
  }, [studentsWithAttendance, selectedStage, enabledStages]);

  // Overall statistics مع cache
  const overallStats = useMemo(() => {
    // const cacheKey = `stats-${attendanceRecords.length}`;
    // const cached = cache.get(cacheKey);
    // if (cached) return cached;
    
    const result = calculateOverallStats(attendanceRecords);

    // cache.set(cacheKey, result);
    return result;
  }, [attendanceRecords]);


  return {
    filteredStudents,
    studentsWithAlerts,
    studentsWithLateAlerts,
    mostAbsentStudents,
    mostLateStudents,
    bestAttendanceStudents,
    weeklyData,
    gradeData,
    overallStats
  };
};
